// -------------------- restaurantStore.ts --------------------
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { RestaurantSection, Cart, Restaurant } from '@/types/inde_menu';
import { apiService, RestaurantFilters, SearchParams } from '@/lib/api/apiService';

interface RestaurantState {
    // State
    socket: any | null;
    restaurants: Restaurant[];
    selectedRestaurant: Restaurant | null;
    featuredRestaurants: Restaurant[];
    nearbyRestaurants: Restaurant[];
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    filters: RestaurantFilters;
    sort: string | null;
    cart: Cart | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };

    // Admin operations
    createRestaurant: (restaurantData: Omit<Restaurant, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>) => Promise<Restaurant>;
    updateRestaurant: (id: string, updates: Partial<Restaurant>) => Promise<Restaurant>;
    deleteRestaurant: (id: string) => Promise<void>;
    toggleRestaurantStatus: (id: string, status: Restaurant['status']) => Promise<Restaurant>;
    assignStaffToRestaurant: (restaurantId: string, staffId: string, role: string) => Promise<Restaurant>;
    removeStaffFromRestaurant: (restaurantId: string, staffId: string) => Promise<Restaurant>; 
    updateRestaurantMenu: (restaurantId: string, menuId: string, action: 'add' | 'remove') => Promise<Restaurant>;
    updateRestaurantSections: (restaurantId: string, sectionId: string, action: 'add' | 'remove') => Promise<Restaurant>;
    updateRestaurantFeatures: (restaurantId: string, features: string[]) => Promise<Restaurant>;

    // Customer operations
    initializeSocket: () => any;
    disconnectSocket: () => void;
    fetchRestaurants: (params?: SearchParams) => Promise<Restaurant[]>;
    fetchRestaurantById: (id: string) => Promise<Restaurant | null>;
    fetchFeaturedRestaurants: () => Promise<Restaurant[]>;
    updateRestaurantList: (restaurants: Restaurant[]) => void;
    fetchNearbyRestaurants: (lat: number, lng: number, radius?: number) => Promise<Restaurant[]>;
    updateFilters: (filters: Partial<RestaurantFilters>) => void;
    setSort: (sort: string | null) => void;
    searchRestaurants: (query: string, filters?: Partial<SearchParams>) => Promise<Restaurant[]>;
    fetchRestaurantSections: (restaurantId: string) => Promise<RestaurantSection[]>;
 
    // Utility methods
    clearError: () => void;
    clearRestaurants: () => void;
    resetState: () => void;
}

const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL || "https://indemenu.com/api/v1";

const initialState = {
    socket: null,
    restaurants: [],
    selectedRestaurant: null,
    featuredRestaurants: [],
    nearbyRestaurants: [],
    isLoading: false,
    isSubmitting: false,
    error: null,
    filters: {
        cuisineType: [],
        priceRange: [],
        features: [],
        rating: null,
        openNow: false,
        acceptsReservations: false,
        deliveryAvailable: false,
        takeawayAvailable: false
    },
    sort: null,
    cart: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    }
};

export const useRestaurantStore = create<RestaurantState>()(
    persist(
        (set, get) => ({
            ...initialState,

            // -------------------- Admin Operations --------------------
            createRestaurant: async (restaurantData) => {
                set({ isSubmitting: true, error: null });
                try {
                    const newRestaurant = await apiService.createRestaurant(restaurantData);

                    set(state => ({
                        restaurants: [newRestaurant, ...state.restaurants],
                        isSubmitting: false
                    }));

                    return newRestaurant;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to create restaurant';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            updateRestaurant: async (id, updates) => {
                set({ isSubmitting: true, error: null });
                try {
                    const updatedRestaurant = await apiService.updateRestaurant(id, updates);

                    set(state => ({
                        restaurants: state.restaurants.map(r =>
                            r.id === id ? updatedRestaurant : r
                        ),
                        selectedRestaurant: state.selectedRestaurant?.id === id
                            ? updatedRestaurant
                            : state.selectedRestaurant,
                        isSubmitting: false
                    }));

                    return updatedRestaurant;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to update restaurant';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            deleteRestaurant: async (id) => {
                set({ isSubmitting: true, error: null });
                try {
                    await apiService.deleteRestaurant(id);

                    set(state => ({
                        restaurants: state.restaurants.filter(r => r.id !== id),
                        selectedRestaurant: state.selectedRestaurant?.id === id
                            ? null
                            : state.selectedRestaurant,
                    }));
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to delete restaurant';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            toggleRestaurantStatus: async (id: string, status: 'active' | 'closed' | 'temporary-closed' | 'pending-approval') => {
                try {
                    const updatedRestaurant = await apiService.updateRestaurantStatus(id, status);
                    return get().updateRestaurant(id, { status: status as any });
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to toggle restaurant status';
                    set({ error: errorMessage });
                    throw new Error(errorMessage);
                }
            },

            assignStaffToRestaurant: async (restaurantId, staffId, role) => {
                set({ isSubmitting: true, error: null });
                try {
                    const updatedRestaurant = await apiService.assignStaffToRestaurant(restaurantId, staffId, role);

                    set(state => ({
                        restaurants: state.restaurants.map(r =>
                            r.id === restaurantId ? updatedRestaurant : r
                        ),
                        selectedRestaurant: state.selectedRestaurant?.id === restaurantId
                            ? updatedRestaurant
                            : state.selectedRestaurant,
                        isSubmitting: false
                    }));

                    return updatedRestaurant;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to assign staff';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            removeStaffFromRestaurant: async (restaurantId, staffId) => {
                set({ isSubmitting: true, error: null });
                try {
                    const updatedRestaurant = await apiService.removeStaffFromRestaurant(restaurantId, staffId);

                    set(state => ({
                        restaurants: state.restaurants.map(r =>
                            r.id === restaurantId ? updatedRestaurant : r
                        ),
                        selectedRestaurant: state.selectedRestaurant?.id === restaurantId
                            ? updatedRestaurant
                            : state.selectedRestaurant,
                        isSubmitting: false
                    }));

                    return updatedRestaurant;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to remove staff';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            }, 
            updateRestaurantMenu: async (restaurantId, menuId, action) => {
                set({ isSubmitting: true, error: null });
                try {
                    const restaurant = get().restaurants.find(r => r.id === restaurantId);
                    if (!restaurant) {
                        throw new Error('Restaurant not found');
                    }

                    const currentMenuIds = restaurant.menuIds || [];
                    let updatedMenuIds: string[];

                    if (action === 'add') {
                        updatedMenuIds = [...currentMenuIds, menuId];
                    } else {
                        updatedMenuIds = currentMenuIds.filter((id: string) => id !== menuId);
                    }

                    return get().updateRestaurant(restaurantId, { menuIds: updatedMenuIds });
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to update menu';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            updateRestaurantSections: async (restaurantId, sectionId, action) => {
                set({ isSubmitting: true, error: null });
                try {
                    const restaurant = get().restaurants.find(r => r.id === restaurantId);
                    if (!restaurant) {
                        throw new Error('Restaurant not found');
                    }

                    const currentSectionIds = restaurant.sectionIds || [];
                    let updatedSectionIds: string[];

                    if (action === 'add') {
                        updatedSectionIds = [...currentSectionIds, sectionId];
                    } else {
                        updatedSectionIds = currentSectionIds.filter((id: string) => id !== sectionId);
                    }

                    return get().updateRestaurant(restaurantId, { sectionIds: updatedSectionIds });
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to update sections';
                    set({ error: errorMessage, isSubmitting: false });
                    throw new Error(errorMessage);
                }
            },

            updateRestaurantFeatures: async (restaurantId, features) => {
                return get().updateRestaurant(restaurantId, { features });
            },

            // -------------------- Customer Operations --------------------
            initializeSocket: () => {
                console.log('Real-time socket handled by client SocketService');
                return null as any;
            },

            disconnectSocket: () => {
                console.log('Real-time socket handled by client SocketService');
            },

            fetchRestaurants: async (params?: SearchParams) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await apiService.getRestaurants({
                        page: 1,
                        limit: 50,
                        ...params
                    });

                    set({
                        restaurants: response.data,
                        pagination: response.pagination,
                        isLoading: false
                    });

                    return response.data;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch restaurants';
                    set({ error: errorMessage, isLoading: false });
                    return [];
                }
            },

            fetchRestaurantById: async (id: string) => {
                set({ isLoading: true, error: null });
                try {
                    const restaurant = await apiService.getRestaurant(id);

                    if (!restaurant) {
                        set({ error: 'Restaurant not found', isLoading: false });
                        return null;
                    }

                    set({ selectedRestaurant: restaurant, isLoading: false });
                    return restaurant;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch restaurant';
                    set({ error: errorMessage, isLoading: false });
                    return null;
                }
            },

            fetchFeaturedRestaurants: async () => {
                set({ isLoading: true, error: null });
                try {
                    const featured = await apiService.getFeaturedRestaurants();

                    set({ featuredRestaurants: featured, isLoading: false });
                    return featured;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch featured restaurants';
                    set({ error: errorMessage, isLoading: false });
                    return [];
                }
            },

            updateRestaurantList: (restaurants: Restaurant[]) => {
                set({ restaurants });
            },

            fetchNearbyRestaurants: async (lat: number, lng: number, radius = 5) => {
                set({ isLoading: true, error: null });
                try {
                    const nearby = await apiService.getNearbyRestaurants(lat, lng, radius);

                    set({ nearbyRestaurants: nearby, isLoading: false });
                    return nearby;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch nearby restaurants';
                    set({ error: errorMessage, isLoading: false });
                    return [];
                }
            },

            updateFilters: (filters: Partial<RestaurantFilters>) => {
                set(state => ({
                    filters: { ...state.filters, ...filters }
                }));
            },

            setSort: (sort: string | null) => {
                set({ sort });
            },

            searchRestaurants: async (query: string, filters?: Partial<SearchParams>) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await apiService.searchRestaurants(query, filters);

                    set({
                        restaurants: response.data,
                        pagination: response.pagination,
                        isLoading: false
                    });

                    return response.data;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to search restaurants';
                    set({ error: errorMessage, isLoading: false });
                    return [];
                }
            },

            fetchRestaurantSections: async (restaurantId: string) => {
                set({ isLoading: true, error: null });
                try {
                    const sections = await apiService.getRestaurantSections(restaurantId);

                    set({ isLoading: false });
                    return sections;
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch restaurant sections';
                    set({ error: errorMessage, isLoading: false });
                    return [];
                }
            },
 

            // -------------------- Utility Methods --------------------
            clearError: () => {
                set({ error: null });
            },

            clearRestaurants: () => {
                set({ restaurants: [] });
            },

            resetState: () => {
                set(initialState);
            }
        }),
        {
            name: 'restaurant-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                filters: state.filters,
                sort: state.sort,
                cart: state.cart,
                // NOTE: Server state (restaurants, featuredRestaurants, nearbyRestaurants)
                // is intentionally excluded from localStorage persistence.
                // Per ISO 27001 and stack_prompt.md compliance, server data must only
                // live in TanStack Query's in-memory cache with proper stale/gc times.
            }),
            version: 1,
            migrate: (persistedState: any, version: number) => {
                if (version === 0) {
                    // Migration from version 0 to 1
                    return {
                        ...persistedState,
                        pagination: initialState.pagination
                    };
                }
                return persistedState;
            }
        }
    )
);

// -------------------- Custom Hooks for Common Operations --------------------
export const useRestaurantActions = () => {
    const store = useRestaurantStore();

    return {
        // Admin actions
        createRestaurant: store.createRestaurant,
        updateRestaurant: store.updateRestaurant,
        deleteRestaurant: store.deleteRestaurant,

        // Customer actions
        searchRestaurants: store.searchRestaurants,
        fetchRestaurantById: store.fetchRestaurantById,
        fetchRestaurants: store.fetchRestaurants,
        // Utility
        clearError: store.clearError,
        resetState: store.resetState
    };
};

export const useRestaurantData = () => {
    const store = useRestaurantStore();

    return {
        // State
        restaurants: store.restaurants,
        selectedRestaurant: store.selectedRestaurant,
        featuredRestaurants: store.featuredRestaurants,
        nearbyRestaurants: store.nearbyRestaurants,
        isLoading: store.isLoading,
        isSubmitting: store.isSubmitting,
        error: store.error,
        filters: store.filters,
        cart: store.cart,
        pagination: store.pagination,

        // Derived state
        hasRestaurants: store.restaurants.length > 0,
        hasCartItems: store.cart ? store.cart.items.length > 0 : false,
        cartTotal: store.cart?.total || 0,
        cartItemCount: store.cart?.items.reduce((total, item) => total + item.quantity, 0) || 0
    };
};